// src/components/EditProviderDialog.js

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/constants';
import { usePermissions } from '../context/PermissionContext';

const EditProviderDialog = ({ open, onClose, provider, showSnackbar, refreshData }) => {
  const { can } = usePermissions();
  const [name, setName] = useState(provider.name || '');
  const [email, setEmail] = useState(provider.email || '');
  const [phoneNumber, setPhoneNumber] = useState(provider.phoneNumber || '');
  const [whatsapp, setWhatsapp] = useState(provider.whatsapp || '');
  const [address, setAddress] = useState(provider.address || '');
  const [pincode, setPincode] = useState(provider.pincode || '');

  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);

  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (open) {
    //   fetchDistricts();
      // Reset form errors
      setFormErrors({});
      // Initialize fields with provider data
      setName(provider.name || '');
      setEmail(provider.email || '');
      setPhoneNumber(provider.phoneNumber || '');
      setWhatsapp(provider.whatsapp || '');
      setAddress(provider.address || '');
      setPincode(provider.pincode || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, provider]);

  useEffect(() => {
    if (pincode && /^\d{6}$/.test(pincode)) {
      fetchPincodeDetails(pincode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pincode]);

  const fetchDistricts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/districts`);
      if (response.status === 200) {
        setDistricts(response.data.districts || response.data);
      } else {
        console.error('Failed to fetch districts');
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchTaluks = async (districtId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/taluks/district/${districtId}`);
      if (response.status === 200) {
        setTaluks(response.data.taluks || response.data);
      } else {
        setTaluks([]);
        console.error('Failed to fetch taluks');
      }
    } catch (error) {
      setTaluks([]);
      console.error('Error fetching taluks:', error);
    }
  };

  const fetchPincodeDetails = async (pincode) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/pincodes/${pincode}`);
      if (response.status === 200) {
        const { districtId, talukId } = response.data;
        // Fetch taluks based on districtId to ensure consistency
        await fetchTaluks(districtId);
      } else {
        showSnackbar('Pincode not found', 'error');
      }
    } catch (error) {
      console.error('Error fetching pincode details:', error);
      showSnackbar('Error fetching pincode details', 'error');
    }
  };

//   Define handlePincodeBlur before using it in JSX
  const handlePincodeBlur = async () => {
    if (pincode && /^\d{6}$/.test(pincode)) {
    //   await fetchPincodeDetails(pincode);
    }
  };

  const validateForm = () => {
    const errors = {};
    const phoneRegex = /^[6-9]\d{9}$/;
    const emailRegex = /^\S+@\S+\.\S+$/;
    const pincodeRegex = /^\d{6}$/;

    if (!name.trim()) errors.name = 'Name is required';
    if (email && !emailRegex.test(email)) errors.email = 'Invalid email format';
    if (!phoneNumber) {
      errors.phoneNumber = 'Phone number is required';
    } else if (!phoneRegex.test(phoneNumber)) {
      errors.phoneNumber = 'Phone number must be 10 digits';
    }
    if (whatsapp && !phoneRegex.test(whatsapp)) {
      errors.whatsapp = 'WhatsApp number must be 10 digits';
    }
    if (!address.trim()) errors.address = 'Address is required';
    if (!pincode) {
      errors.pincode = 'Pincode is required';
    } else if (!pincodeRegex.test(pincode)) {
      errors.pincode = 'Pincode must be 6 digits';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!can('ads.providers.edit')) return;
    if (!validateForm()) {
      showSnackbar('Please fix the errors in the form', 'error');
      return;
    }

    setLoading(true);
    try {
      const updatedProvider = {
        name,
        email: email || undefined,
        phoneNumber,
        whatsapp: whatsapp || undefined,
        address,
        pincode,
      };

      const response = await axios.put(`${API_BASE_URL}/providers/${provider._id}`, updatedProvider);
      if (response.status === 200) {
        showSnackbar('Provider updated successfully', 'success');
        onClose();
        refreshData();
      } else {
        showSnackbar('Failed to update provider', 'error');
      }
    } catch (error) {
      console.error('Error updating provider:', error);
      showSnackbar(error.response?.data?.message || 'Error updating provider', 'error');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Provider</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ marginTop: '10px' }}>
          {/* Name */}
          <Grid item xs={12}>
            <TextField
              required
              label="Name"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12}>
            <TextField
              label="Email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
          </Grid>

          {/* Phone Number */}
          <Grid item xs={12}>
            <TextField
              required
              label="Phone Number"
              fullWidth
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              error={!!formErrors.phoneNumber}
              helperText={formErrors.phoneNumber}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          {/* WhatsApp Number */}
          <Grid item xs={12}>
            <TextField
              label="WhatsApp Number"
              fullWidth
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              error={!!formErrors.whatsapp}
              helperText={formErrors.whatsapp}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12}>
            <TextField
              required
              label="Address"
              fullWidth
              multiline
              minRows={2}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              error={!!formErrors.address}
              helperText={formErrors.address}
            />
          </Grid>

          {/* Pincode */}
          <Grid item xs={12} sm={6}>
            <TextField
              required
              label="Pincode"
              fullWidth
              value={pincode}
              onChange={(e) => setPincode(e.target.value)}
              onBlur={handlePincodeBlur}
              error={!!formErrors.pincode}
              helperText={formErrors.pincode}
              inputProps={{ maxLength: 6 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Button onClick={onClose} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Save
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditProviderDialog;
